/* ============================================================================
 * cabinet-recolor.js  —  RTA Wholesalers cabinet color visualizer engine (v2)
 *
 * MASK-BASED. Each product photo has a pre-generated silhouette mask, built by
 * scripts/usamca/generate_recolor_masks.py (rembg + clean-up) and served from
 * opcabinets.com/recolor-masks/ with CORS headers. The engine recolors ONLY
 * inside the mask:
 *   - FRONT (face frame + drawer fronts + doors)  -> customer's SW color
 *   - BOX  (warm wood: sides / interior / toe-kick) -> locked Accessible Beige
 *
 * If an image has NO mask in the manifest, it is NOT recolored at all — the
 * original photo stays. Nothing ugly can ship: no mask, no paint.
 * ==========================================================================*/
window.CabinetRecolor = (function () {
  'use strict';

  var BOX_BEIGE = [0xD1, 0xC7, 0xB8];   // Accessible Beige SW 7036 (locked box color)
  var LP = 0.84;                         // luminance treated as a flat painted face
  var HI = 0.18;                         // how far highlights lift toward white
  var FLOOR = 0.40;                      // darkest shadow keeps this fraction of the color
  var STORE_KEY = 'rta_front_color';
  var MASK_BASE = window.RTA_MASK_BASE || 'https://opcabinets.com/recolor-masks/';

  function hexToRgb(hex) {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  }

  /* --- mask manifest (fetched once per page) ------------------------------ */
  var manifestPromise = null;
  function manifest() {
    if (!manifestPromise) {
      manifestPromise = fetch(MASK_BASE + 'manifest.json')
        .then(function (r) { return r.ok ? r.json() : {}; })
        .catch(function () { return {}; });
    }
    return manifestPromise;
  }

  // CDN filename -> mask key. Mirrors mask_key() in the Python generator.
  function maskKey(url) {
    var name = String(url || '').split('?')[0];
    name = name.substring(name.lastIndexOf('/') + 1);
    name = name.replace(/\.(jpe?g|png|webp|gif)$/i, '');
    name = name.replace(/_(\d+x\d*|\d*x\d+|pico|icon|thumb|small|compact|medium|large|grande|original|master)$/i, '');
    return name;
  }

  /* --- segmentation from a mask ------------------------------------------- */
  function segmentWithMask(img, maskImg, size) {
    var maxDim = size || 760;
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    var scale = Math.min(1, maxDim / Math.max(iw, ih));
    var W = Math.max(1, Math.round(iw * scale)), H = Math.max(1, Math.round(ih * scale));
    var N = W * H;

    var pc = document.createElement('canvas'); pc.width = W; pc.height = H;
    var pcx = pc.getContext('2d', { willReadFrequently: true });
    pcx.drawImage(img, 0, 0, W, H);
    var d = pcx.getImageData(0, 0, W, H).data;

    var mc = document.createElement('canvas'); mc.width = W; mc.height = H;
    var mcx = mc.getContext('2d', { willReadFrequently: true });
    mcx.drawImage(maskImg, 0, 0, W, H);
    var md = mcx.getImageData(0, 0, W, H).data;

    var alpha = new Uint8Array(N), wood = new Uint8Array(N), lum = new Float32Array(N);
    for (var i = 0; i < N; i++) {
      var k = i * 4;
      // masks are grayscale: red channel == alpha level
      alpha[i] = md[k];
      var r = d[k], g = d[k + 1], b = d[k + 2];
      lum[i] = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      if (alpha[i] > 100 && (r - b) > 34 && r >= g - 4 && g >= b - 4) wood[i] = 1;
    }
    // smooth the wood mask so front/box regions are coherent, not patchy
    var wnext = new Uint8Array(wood);
    for (var wi = 0; wi < N; wi++) {
      var wx = wi % W, wy = (wi - wx) / W;
      if (wx === 0 || wy === 0 || wx === W - 1 || wy === H - 1 || alpha[wi] <= 100) continue;
      var ws = wood[wi - 1] + wood[wi + 1] + wood[wi - W] + wood[wi + W] +
               wood[wi - W - 1] + wood[wi - W + 1] + wood[wi + W - 1] + wood[wi + W + 1];
      if (!wood[wi] && ws >= 6) wnext[wi] = 1;
      else if (wood[wi] && ws <= 2) wnext[wi] = 0;
    }
    wood = wnext;
    return { W: W, H: H, N: N, alpha: alpha, wood: wood, lum: lum, out: pcx.createImageData(W, H) };
  }

  // luminance-preserving repaint of one pixel onto a target color
  function paintPx(L, col) {
    if (L <= LP) { var f = FLOOR + (1 - FLOOR) * (L / LP); return [col[0] * f, col[1] * f, col[2] * f]; }
    var t = Math.min((L - LP) / (1 - LP), 1) * HI;
    return [col[0] + (255 - col[0]) * t, col[1] + (255 - col[1]) * t, col[2] + (255 - col[2]) * t];
  }

  function repaint(canvas, seg, frontHex, opts) {
    opts = opts || {};
    var box = opts.box || BOX_BEIGE;
    var FRONT = hexToRgb(frontHex);
    var W = seg.W, H = seg.H, N = seg.N, o = seg.out.data;
    canvas.width = W; canvas.height = H;
    for (var i = 0; i < N; i++) {
      var k = i * 4, a = seg.alpha[i];
      if (a < 12) { o[k + 3] = 0; continue; }         // outside the mask: transparent
      var col = seg.wood[i] ? box : FRONT;
      var pp = paintPx(seg.lum[i], col);
      o[k] = pp[0] | 0; o[k + 1] = pp[1] | 0; o[k + 2] = pp[2] | 0;
      o[k + 3] = a;                                    // feathered edges from the mask
    }
    canvas.getContext('2d').putImageData(seg.out, 0, 0);
  }

  /* --- Instance: mask lookup + segment on load, then setColor() any time --- */
  function Instance(img, canvas, opts) {
    this.canvas = canvas; this.opts = opts || {};
    this.ready = false; this.disabled = false; this._pending = null;
    var self = this;

    function withPhoto(fn) {
      if (img.complete && img.naturalWidth) fn();
      else img.addEventListener('load', fn, { once: true });
    }
    manifest().then(function (map) {
      var entry = map[maskKey(img.src)];
      if (!entry) { self.disabled = true; return; }
      var maskImg = new Image();
      maskImg.crossOrigin = 'anonymous';
      maskImg.onload = function () {
        withPhoto(function () {
          self.seg = segmentWithMask(img, maskImg, self.opts.size || 760);
          self.ready = true;
          if (self._pending) self.setColor(self._pending);
          if (self.opts.onReady) self.opts.onReady(self);
        });
      };
      maskImg.onerror = function () { self.disabled = true; };
      maskImg.src = MASK_BASE + entry.file;
    });
  }
  Instance.prototype.setColor = function (hex) {
    if (this.disabled) return;
    if (!this.ready) { this._pending = hex; return; }
    repaint(this.canvas, this.seg, hex, this.opts); this.hex = hex;
  };
  Instance.prototype.snapshotDataURL = function (type) { return this.canvas.toDataURL(type || 'image/png'); };
  Instance.prototype.snapshotBlob = function (cb, type) { this.canvas.toBlob(cb, type || 'image/png'); };

  /* --- persistence --------------------------------------------------------- */
  function save(sel) { try { localStorage.setItem(STORE_KEY, JSON.stringify(sel)); } catch (e) {} }
  function load() { try { return JSON.parse(localStorage.getItem(STORE_KEY)); } catch (e) { return null; } }
  function clear() { try { localStorage.removeItem(STORE_KEY); } catch (e) {} }

  // Write the chosen color as a Shopify cart attribute (carries to checkout/order).
  function saveToCart(sel) {
    return fetch('/cart/update.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Front color': sel.name + ' ' + sel.code } })
    });
  }

  /* --- skip diagrams / spec art -------------------------------------------- */
  function shouldSkip(imgEl) {
    if (imgEl.dataset && imgEl.dataset.noRecolor !== undefined) return true;
    var src = (imgEl.getAttribute('src') || '').toLowerCase();
    var alt = (imgEl.getAttribute('alt') || '').toLowerCase();
    return /-spec|-diagram|diagram|dimension/.test(src + ' ' + alt);
  }

  return {
    BOX_BEIGE: BOX_BEIGE, STORE_KEY: STORE_KEY, MASK_BASE: MASK_BASE,
    maskKey: maskKey, Instance: Instance,
    save: save, load: load, clear: clear, saveToCart: saveToCart, shouldSkip: shouldSkip
  };
})();